import '../../i18n/autoTranslate';
import React from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  MessageSquare,
  Monitor,
  Sparkles,
  Target,
} from 'lucide-react';
import { motion } from 'framer-motion';

const StepProgressIndicator = ({
  currentStep,
  totalSteps,
  steps,
  onStepClick,
  className = '',
}) => {
  const defaultSteps = [
    { id: 'type', label: 'Interview Type' },
    { id: 'targeting', label: 'Role Targeting' },
    { id: 'personality', label: 'AI Personality' },
    { id: 'questions', label: 'Questions' },
    { id: 'format', label: 'Format' },
    { id: 'summary', label: 'Review' },
  ];

  const stepList = steps || defaultSteps;
  const progressPercentage = ((currentStep + 1) / stepList?.length) * 100;

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepClasses = (status) => {
    const base =
      'relative overflow-hidden w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border flex items-center justify-center backdrop-blur-xl transition-all duration-300';

    if (status === 'completed') {
      return (
        base +
        ' bg-emerald-400/10 border-emerald-300/20 text-white shadow-[0_0_0_1px_rgba(16,185,129,0.10),0_14px_40px_-22px_rgba(16,185,129,0.55)]'
      );
    }

    if (status === 'current') {
      return (
        base +
        " bg-gradient-to-br from-violet-500/30 via-sky-500/20 to-emerald-400/15 border-white/15 text-white shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_18px_55px_-28px_rgba(99,102,241,0.55)]"
      );
    }

    return (
      base +
      ' bg-white/[0.03] border-white/10 text-white/75 hover:border-white/15 hover:bg-white/[0.045]'
    );
  };

  const getStepIcon = (stepId) => {
    switch (stepId) {
      case 'type':
        return MessageSquare;
      case 'targeting':
        return Target;
      case 'personality':
        return Sparkles;
      case 'questions':
        return HelpCircle;
      case 'format':
        return Monitor;
      case 'summary':
        return Check;
      default:
        return MessageSquare;
    }
  };

  const renderStepIcon = (step, status) => {
    if (status === 'completed') return <Check className="h-5 w-5" />;
    const StepIcon = getStepIcon(step?.id);
    return <StepIcon className="h-6 w-6 text-white" />;
  };

  return (
    <div className={`space-y-5 ${className}`}>
      {/* Progress Bar */}
      <div className="relative">
        <div className="w-full rounded-full h-2 bg-white/10 overflow-hidden">
          <motion.div
            className="relative h-2 rounded-full bg-gradient-to-r from-violet-400/70 via-sky-300/70 to-emerald-300/70"
            initial={false}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ type: 'spring', stiffness: 120, damping: 20 }}
          >
            <div className="absolute inset-0 opacity-60 [background:radial-gradient(circle_at_30%_60%,rgba(255,255,255,0.65),transparent_55%)]" />
          </motion.div>
        </div>
      </div>

      {/* Desktop Steps */}
      <div className="hidden md:flex items-center justify-between">
        {stepList?.map((step, index) => {
          const status = getStepStatus(index);
          const isClickable = index <= currentStep;

          return (
            <React.Fragment key={step?.id}>
              <motion.button
                type="button"
                onClick={() => isClickable && onStepClick && onStepClick(index)}
                disabled={!isClickable}
                className={`flex flex-col items-center gap-2 ${
                  isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'
                }`}
                whileHover={isClickable ? { y: -2 } : undefined}
                whileTap={isClickable ? { scale: 0.98 } : undefined}
                transition={{ type: 'spring', stiffness: 250, damping: 18 }}
              >
                <div className={getStepClasses(status)}>
                  {/* inner glow */}
                  <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.18),transparent_55%)]" />
                  <div className="relative z-10">{renderStepIcon(step, status)}</div>
                </div>

                <div
                  className={`text-center text-sm font-medium transition-colors duration-200 ${
                    status === 'current'
                      ? 'text-white'
                      : status === 'completed'
                      ? 'text-emerald-200'
                      : 'text-white/55'
                  }`}
                >
                  {step?.label}
                </div>
              </motion.button>

              {index < stepList?.length - 1 && (
                <div
                  className={`flex-1 mx-4 h-px rounded-full transition-colors duration-300 ${
                    index < currentStep
                      ? 'bg-emerald-300/35'
                      : 'bg-white/10'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile Steps */}
      <div className="md:hidden">
        <div className="flex items-center justify-between gap-3">
          {/* Previous */}
          <button
            type="button"
            onClick={() => currentStep > 0 && onStepClick && onStepClick(currentStep - 1)}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 text-xs transition-colors ${
              currentStep === 0
                ? 'text-white/30'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden xs:inline">Prev</span>
          </button>

          {/* Current */}
          <div className="flex items-center gap-3">
            <div className={getStepClasses('current')}>
              <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.18),transparent_55%)]" />
              <div className="relative z-10">{renderStepIcon(stepList?.[currentStep], 'current')}</div>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white truncate">
                {stepList?.[currentStep]?.label}
              </div>
              <div className="text-xs text-white/55">
                Step {currentStep + 1} of {stepList?.length}
              </div>
            </div>
          </div>

          {/* Next */}
          <button
            type="button"
            onClick={() => currentStep < stepList?.length - 1 && onStepClick && onStepClick(currentStep + 1)}
            disabled={currentStep >= stepList?.length - 1}
            className={`flex items-center gap-2 text-xs transition-colors ${
              currentStep >= stepList?.length - 1
                ? 'text-white/30'
                : 'text-white/60 hover:text-white'
            }`}
          >
            <span className="hidden xs:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepProgressIndicator;
